import "react-toastify/dist/ReactToastify.css";

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Check, Lock, Plus } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import JoditEditor from "jodit-react";
import { ClipLoader } from "react-spinners";
import { toast } from "../../utils/toast";

import AuthContext from "../../contexts/AuthCtx";
import BuyStampsModal from "../../components/Dashboard/BuyStampsModal";
import DeleteLetterModal from "../../components/Dashboard/DeleteLetterModal";
import SendNowModal from "../../components/Dashboard/SendNowModal";
import DeleteRecipientModal from "../../components/Letter/DeleteRecipientModal";
import InternetConnectionAlert from "../../components/UX/InternetConnectionAlert";
import LetterEditorSidebar from "../../components/Letter/LetterEditorSidebar";
import LetterFormattingDrawer from "../../components/Letter/LetterFormattingDrawer";
import LetterMobileToolbar from "../../components/Letter/LetterMobileToolbar";
import LetterRecipientsBar from "../../components/Letter/LetterRecipientsBar";
import LetterRecipientsSheet from "../../components/Letter/LetterRecipientsSheet";
import NoRecipientBanner from "../../components/Letter/NoRecipientBanner";
import RecipientModal from "../../components/Letter/RecipientModal";
import ScheduledSendingModal from "../../components/Letter/ScheduledSendingModal";
import TitleModal from "../../components/Letter/TitleModal";
import { api } from "../../api/api";
import {
  applyEditorWorkspaceStyle,
  bindPendingTypingStyle,
  clearPendingTypingStyle,
  saveEditorSelection,
  setEditorReadOnly,
  unbindPendingTypingStyle,
} from "../../utils/joditHelpers";
import {
  buildEditorFontStyle,
  DEFAULT_LETTER_PREFERENCES,
  getTextStyleSize,
  preferencesFromLetter,
  savePreferencesLocally,
} from "../../utils/letterPreferences";
import {
  formatRecipientName,
  getImmediateRecipients,
  getScheduledRecipients,
} from "../../utils/letterBoard";
import {
  canSendLetterNow,
  isLetterFullySent,
  isLifetimeRecipient,
  LETTER_STATUS,
  recipientReceivedRingClass,
  SEND_MODE,
} from "../../utils/letterSend";
import { captureEvent } from "../../analytics";

function Letter() {
  const AuthCtx = useContext(AuthContext);
  const { selectedLetter } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const joditInstance = useRef(null);
  const preferencesRef = useRef({ ...DEFAULT_LETTER_PREFERENCES });
  const isReadOnlyRef = useRef(false);
  // Opened from the Envoyées column (incl. partial send) → read-only sent view.
  const isSentView = searchParams.get("view") === "sent";
  const saveTimeoutRef = useRef(null);
  const latestContentRef = useRef("");
  const updateLetterHandlerRef = useRef(null);
  // Seed passed to Jodit only on letter load — feeding onChange back as value resets caret.
  const [editorSeed, setEditorSeed] = useState("");

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWriting, setIsWriting] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [title, setTitle] = useState("");
  const [letterStatus, setLetterStatus] = useState(LETTER_STATUS.DRAFT);
  const [preferences, setPreferences] = useState({ ...DEFAULT_LETTER_PREFERENCES });

  const [titleModalState, setTitleModalState] = useState(false);
  const [deleteModalState, setDeleteModalState] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const [recipients, setRecipients] = useState([]);
  const [recepientChanged, setRecepientChanged] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [recepientEditing, setRecepientEditing] = useState(false);
  const [recepientModalState, setRecepientModalState] = useState(false);
  const [isBuyStampsModalOpen, setIsBuyStampsModalOpen] = useState(false);
  const [scheduledModalState, setScheduledModalState] = useState(false);
  const [sendNowModalState, setSendNowModalState] = useState(false);
  const [recipientDeleteModalState, setRecipientDeleteModalState] = useState(false);
  const [formatDrawerOpen, setFormatDrawerOpen] = useState(false);
  const [recipientsSheetOpen, setRecipientsSheetOpen] = useState(false);

  const applyPreferencesToEditor = useCallback((prefs) => {
    // Workspace root stays at body size so unstyled text inherits "Texte",
    // not the last Titre/Sous-titre selection. Per-block sizes live in HTML.
    applyEditorWorkspaceStyle(
      joditInstance.current,
      prefs.fontFamily,
      getTextStyleSize(DEFAULT_LETTER_PREFERENCES.textStyle)
    );
  }, []);

  const savePreferences = useCallback(
    async (nextPrefs, options = {}) => {
      if (isReadOnlyRef.current) return;
      const { applyToWorkspace = true } = options;
      preferencesRef.current = nextPrefs;
      setPreferences(nextPrefs);
      if (applyToWorkspace) {
        applyPreferencesToEditor(nextPrefs);
      }
      savePreferencesLocally(selectedLetter, nextPrefs);

      try {
        await api.put(`letter/update/${selectedLetter}`, {
          fontFamily: nextPrefs.fontFamily,
          textStyle: nextPrefs.textStyle,
        });
      } catch {
        // Backend may not support yet — localStorage fallback already saved
      }
    },
    [applyPreferencesToEditor, selectedLetter]
  );

  const handlePreferenceChange = useCallback(
    (partial, options) => {
      const next = { ...preferencesRef.current, ...partial };
      savePreferences(next, options);
    },
    [savePreferences]
  );

  const updateLetterHandler = useCallback(
    async (html) => {
      if (isReadOnlyRef.current) return;
      try {
        setIsWriting(true);
        const { data } = await api.put(
          `letter/updateContent/${selectedLetter}`,
          { content: html }
        );
        setIsTyping(false);
        const analytics = data?.analytics;
        if (analytics?.activated) {
          captureEvent("letter_activated", {
            letter_id: selectedLetter,
            content_length: analytics.contentLength,
          });
        }
        if (analytics?.interacted) {
          captureEvent("content_interacted", {
            letter_id: selectedLetter,
            content_length: analytics.contentLength,
            activation_at: analytics.activationAt,
          });
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Erreur de sauvegarde");
      } finally {
        setIsWriting(false);
      }
    },
    [selectedLetter]
  );
  updateLetterHandlerRef.current = updateLetterHandler;

  const getLetter = async () => {
    const response = await api.get(`/letter/${selectedLetter}`, {
      headers: { Authorization: `Bearer ${AuthCtx.userToken}` },
    });
    return response.data?.letter;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedLetter || !AuthCtx.userToken) return;
      setIsLoading(true);
      setEditorReady(false);
      joditInstance.current = null;
      try {
        const letter = await getLetter();
        const html = letter?.content || "";
        setEditorSeed(html);
        latestContentRef.current = html;
        setTitle(letter?.title || "Sans titre");
        setLetterStatus(letter?.status || LETTER_STATUS.DRAFT);
        const loadedPrefs = preferencesFromLetter(letter, selectedLetter);
        setPreferences(loadedPrefs);
        preferencesRef.current = loadedPrefs;
      } catch (error) {
        toast.error(error.response?.data?.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    setTimeout(() => setIsFirstLoad(false), 1500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLetter, AuthCtx.userToken]);

  useEffect(() => {
    setIsFirstLoad(true);
    setBannerDismissed(false);
  }, [selectedLetter]);

  // Apply saved defaults once when the editor becomes ready.
  // Per-selection style changes must NOT restyle the whole document.
  useEffect(() => {
    if (!editorReady) return;
    applyPreferencesToEditor(preferencesRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorReady, applyPreferencesToEditor]);

  // Fully sent letters, or opening from Envoyées (partial send), are read-only.
  // Programmées keeps the same letter editable with remaining recipients only.
  const isReadOnly = isLetterFullySent(letterStatus, recipients) || isSentView;
  isReadOnlyRef.current = isReadOnly;
  const editorRecipients = useMemo(() => {
    if (isSentView) return getImmediateRecipients(recipients);
    if (isReadOnly) return recipients;
    const scheduled = getScheduledRecipients(recipients);
    if (scheduled.length > 0 || recipients.some(isLifetimeRecipient)) {
      return scheduled.length > 0
        ? scheduled
        : recipients.filter((r) => !isLifetimeRecipient(r));
    }
    return recipients;
  }, [recipients, isReadOnly, isSentView]);
  const showBanner = !isReadOnly && !editorRecipients.length && !bannerDismissed;

  useEffect(() => {
    if (!editorReady || !joditInstance.current) return;
    setEditorReadOnly(joditInstance.current, isReadOnly);
  }, [isReadOnly, editorReady]);

  const config = useMemo(
    () => ({
      readonly: false,
      toolbar: false,
      statusbar: false,
      spellcheck: true,
      autofocus: true,
      minHeight: 480,
      width: "100%",
      // Use block paragraphs; avoid forced soft-break / aggressive enter behavior.
      enter: "p",
      enterBlock: "p",
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      // Preserve bold/italic/fonts on paste (was insert_only_text, which stripped all formatting).
      defaultActionOnPaste: "insert_clear_html",
      // Keep caret/selection when focus moves to format UI (mobile sheet / toolbar).
      saveSelectionOnBlur: true,
      placeholder: "Commence à écrire…",
      style: buildEditorFontStyle(
        DEFAULT_LETTER_PREFERENCES.fontFamily,
        DEFAULT_LETTER_PREFERENCES.textStyle
      ),
      events: {
        afterInit: (instance) => {
          joditInstance.current = instance;
          setEditorReadOnly(instance, isReadOnlyRef.current);
          setEditorReady(true);
          clearPendingTypingStyle();
          bindPendingTypingStyle(instance);
          const prefs = preferencesRef.current;
          // Body size on workspace root — selection styles live in content HTML.
          applyEditorWorkspaceStyle(
            instance,
            prefs.fontFamily,
            getTextStyleSize(DEFAULT_LETTER_PREFERENCES.textStyle)
          );
        },
        beforeDestruct: () => {
          if (joditInstance.current) {
            unbindPendingTypingStyle(joditInstance.current);
          }
          clearPendingTypingStyle();
          joditInstance.current = null;
          setEditorReady(false);
        },
        beforePaste: (event) => {
          if (isReadOnlyRef.current) {
            event.preventDefault();
            return;
          }
          const clipboardData = event.clipboardData || window.clipboardData;
          if (clipboardData?.types?.includes("Files")) {
            event.preventDefault();
          }
        },
      },
    }),
    []
  );

  const blurHandler = (value) => {
    if (isReadOnlyRef.current) return;
    latestContentRef.current = value;
  };

  const changeHandler = (value) => {
    if (isReadOnlyRef.current) return;
    // Keep refs in sync for autosave, but do NOT update editorSeed —
    // jodit-react re-applies value on every change and that resets the caret on wrap.
    latestContentRef.current = value;

    if (!navigator.onLine || isFirstLoad) return;

    setIsTyping(true);

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      updateLetterHandler(latestContentRef.current);
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
        // Flush pending autosave so format/content isn't lost when leaving early.
        if (!isReadOnlyRef.current && latestContentRef.current != null) {
          updateLetterHandlerRef.current?.(latestContentRef.current);
        }
      }
    };
  }, []);

  useEffect(() => {
    const fetchRecipients = async () => {
      if (!selectedLetter || !AuthCtx.userToken) return;
      try {
        const response = await api.get(`/letter/getRecipients/${selectedLetter}`, {
          headers: { Authorization: `Bearer ${AuthCtx.userToken}` },
        });
        setRecipients(response.data.recipients || []);
      } catch (error) {
        toast.error(error.response?.data?.message);
      }
    };
    fetchRecipients();
  }, [selectedLetter, recepientChanged, AuthCtx.userToken]);

  const titleSubmitHandler = (newTitle) => {
    setIsWriting(true);
    api
      .put(`letter/update/${selectedLetter}`, { title: newTitle })
      .then(() => {
        setIsTyping(false);
        setTitle(newTitle);
        setTitleModalState(false);
        toast.success("Titre mis à jour");
      })
      .catch((err) => toast.error(err.response?.data?.message))
      .finally(() => setIsWriting(false));
  };

  const closeTitleModal = () => {
    setIsTyping(false);
    setTitleModalState(false);
  };

  const deleteLetterHandler = async () => {
    try {
      await api.delete(`letter/delete/${selectedLetter}`);
      AuthCtx.updateHandler();
      navigate("/dashboard");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const modalSubmitHandler = (data) => {
    if (isReadOnlyRef.current) return;
    if (
      !data.title ||
      !data.firstName?.trim() ||
      !data.lastName?.trim() ||
      !data.country?.trim() ||
      !data.stAndName?.trim() ||
      !data.postalCode?.trim() ||
      !data.city?.trim()
    ) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    const request = recepientEditing
      ? api.put("letter/updateRecipient", {
          recipient: data,
          recipientId: selectedRecipient,
          letterId: selectedLetter,
        })
      : api.post("letter/addRecipient", {
          recipient: { ...data, sendMode: SEND_MODE.POSTHUMOUS },
          letterId: selectedLetter,
        });

    request
      .then(() => {
        setRecepientChanged((prev) => !prev);
        setRecepientModalState(false);
        setRecepientEditing(false);
        if (!recepientEditing) {
          setScheduledModalState(true);
          setLetterStatus(LETTER_STATUS.SCHEDULED);
          AuthCtx.updateHandler();
        }
      })
      .catch((err) => toast.error(err.response?.data?.message));
  };

  const deleteRecipientHandler = async () => {
    try {
      await api.delete(
        `/letter/deleteRecipient/${selectedLetter}/${selectedRecipient}`
      );
      AuthCtx.updateHandler();
      setRecepientChanged((prev) => !prev);
      setRecipientDeleteModalState(false);
      setRecepientModalState(false);
      setRecepientEditing(false);
      setSelectedRecipient(null);
    } catch {
      toast.error("Une erreur s'est produite");
    }
  };

  const confirmSendNow = async (recipientIds) => {
    if (!recipientIds?.length) {
      toast.error("Sélectionne au moins un destinataire.");
      return;
    }

    if (!canSendLetterNow({ status: letterStatus }, recipients)) {
      toast.error("Aucun destinataire disponible pour l'envoi.");
      return;
    }

    try {
      await api.post(`letter/sendNow/${selectedLetter}`, { recipientIds });
      setSendNowModalState(false);
      const letter = await getLetter();
      setLetterStatus(letter?.status || LETTER_STATUS.SCHEDULED);
      setRecepientChanged((prev) => !prev);
      toast.success(
        recipientIds.length === 1
          ? "1 destinataire envoyé."
          : `${recipientIds.length} destinataires envoyés.`
      );
    } catch (err) {
      if (err.response?.status === 404) {
        toast.info(
          "L'envoi immédiat sera disponible dès que le backend sera mis à jour."
        );
        setSendNowModalState(false);
        return;
      }
      toast.error(err.response?.data?.message || "Erreur lors de l'envoi.");
    }
  };

  const openAddRecipient = () => {
    setRecepientEditing(false);
    setSelectedRecipient(null);
    setRecepientModalState(true);
  };

  const openEditRecipient = (recipientId) => {
    setRecepientEditing(true);
    setSelectedRecipient(recipientId);
    setRecepientModalState(true);
  };

  useEffect(() => {
    if (searchParams.get("addRecipient") !== "1") return;
    if (isLoading || isReadOnly) return;
    openAddRecipient();
    const next = new URLSearchParams(searchParams);
    next.delete("addRecipient");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isLoading, isReadOnly]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-pageBg">
        <ClipLoader color="#96B2A3" size={40} />
      </div>
    );
  }

  return (
    <>
      <InternetConnectionAlert
        onLineHandler={() => {
          if (!isReadOnly) updateLetterHandler(latestContentRef.current);
        }}
      />

      <TitleModal
        open={titleModalState}
        isEditing
        id={selectedLetter}
        onFinish={titleSubmitHandler}
        onClose={closeTitleModal}
        onTypingChange={setIsTyping}
      />
      <DeleteLetterModal
        open={deleteModalState}
        onClose={() => setDeleteModalState(false)}
        onConfirm={deleteLetterHandler}
      />
      <DeleteRecipientModal
        open={recipientDeleteModalState}
        onClose={() => setRecipientDeleteModalState(false)}
        onConfirm={deleteRecipientHandler}
      />
      <RecipientModal
        open={recepientModalState}
        isEditing={recepientEditing}
        recipientId={selectedRecipient}
        letterId={selectedLetter}
        onSubmit={modalSubmitHandler}
        onClose={() => setRecepientModalState(false)}
        onBuyStamps={() => {
          setRecepientModalState(false);
          setIsBuyStampsModalOpen(true);
        }}
        onDelete={() => {
          setRecepientModalState(false);
          setRecipientDeleteModalState(true);
        }}
      />
      <BuyStampsModal
        open={isBuyStampsModalOpen}
        onClose={() => setIsBuyStampsModalOpen(false)}
        token={AuthCtx.userToken}
      />
      <ScheduledSendingModal
        open={scheduledModalState}
        onClose={() => setScheduledModalState(false)}
      />
      <SendNowModal
        open={sendNowModalState}
        recipients={recipients}
        onClose={() => setSendNowModalState(false)}
        onConfirm={confirmSendNow}
      />

      <LetterFormattingDrawer
        open={formatDrawerOpen}
        onClose={() => setFormatDrawerOpen(false)}
        editorInstanceRef={joditInstance}
        preferences={preferences}
        onPreferenceChange={handlePreferenceChange}
        editorReady={editorReady}
        readOnly={isReadOnly}
      />
      <LetterRecipientsSheet
        open={recipientsSheetOpen}
        onClose={() => setRecipientsSheetOpen(false)}
        recipients={editorRecipients}
        onAddRecipient={openAddRecipient}
        onEditRecipient={openEditRecipient}
        readOnly={isReadOnly}
      />

      <div className="relative flex h-screen overflow-hidden bg-pageBg lg:bg-white">
        <LetterEditorSidebar
          editorInstanceRef={joditInstance}
          preferences={preferences}
          onPreferenceChange={handlePreferenceChange}
          editorReady={editorReady}
          readOnly={isReadOnly}
        />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-pageBg lg:bg-white">
          {showBanner && (
            <div className="shrink-0 lg:px-8 lg:pt-6">
              <NoRecipientBanner
                compact
                visible
                onDismiss={() => setBannerDismissed(true)}
              />
            </div>
          )}

          {editorRecipients.length > 0 && (
            <LetterRecipientsBar
              letter={{ status: letterStatus }}
              recipients={editorRecipients}
              isReadOnly={isReadOnly}
              onSendNow={() => setSendNowModalState(true)}
            />
          )}

          <header
            className={`flex shrink-0 flex-col gap-4 px-4 pb-3 sm:px-6 sm:pb-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6 lg:px-8 ${
              showBanner ? "pt-3 lg:pt-4" : editorRecipients.length > 0 ? "pt-3 lg:pt-4" : "pt-4 sm:pt-6"
            }`}
          >
            <div className="min-w-0 flex-1 lg:max-w-[min(100%,22rem)]">
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-sm text-secondaryText transition hover:bg-black/[0.04] lg:hidden"
                  onClick={() => navigate("/dashboard")}
                  aria-label="Retour à mes lettres"
                >
                  <ArrowLeft size={20} />
                </button>

                <div className="min-w-0 flex-1">
                  {isReadOnly ? (
                    <h1 className="truncate font-logo text-left text-xl text-black sm:text-2xl lg:text-[1.75rem]">
                      {title}
                    </h1>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setTitleModalState(true)}
                      className="block max-w-full truncate font-logo text-left text-xl text-black underline-offset-4 transition hover:underline hover:opacity-80 sm:text-2xl lg:text-[1.75rem]"
                      aria-label="Modifier le titre"
                      title={title}
                    >
                      {title}
                    </button>
                  )}
                  <p className="mt-1.5 flex min-h-[1.25rem] items-center gap-1.5 font-josefin text-xs text-secondaryText/60">
                    {isReadOnly ? (
                      <>
                        <Lock size={14} className="text-secondaryText/40" />
                        Lecture seule
                      </>
                    ) : isWriting ? (
                      "Enregistrement…"
                    ) : isTyping ? null : (
                      <>
                        <Check size={14} className="text-secondaryText/40" />
                        Modifications enregistrées
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {!isReadOnly && (
              <div className="hidden min-w-0 flex-1 flex-wrap items-center justify-start gap-2 lg:flex lg:justify-end">
                {editorRecipients.map((recipient) => (
                  <button
                    key={recipient._id}
                    type="button"
                    onClick={() => openEditRecipient(recipient._id)}
                    className={`max-w-[14rem] truncate rounded-md bg-sageDark px-4 py-2.5 font-josefin text-sm font-medium text-white transition hover:bg-modalSage sm:px-5 ${recipientReceivedRingClass(recipient, "dark")}`}
                  >
                    {formatRecipientName(recipient)}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={openAddRecipient}
                  className="flex shrink-0 items-center justify-center gap-2 rounded-md bg-primaryDark px-4 py-2.5 font-josefin text-sm text-white transition hover:bg-primaryText sm:px-5"
                >
                  <Plus size={16} />
                  Destinataire
                </button>
              </div>
            )}

            {isReadOnly && editorRecipients.length > 0 && (
              <div className="hidden min-w-0 flex-1 flex-wrap justify-start gap-2 lg:flex lg:justify-end">
                {editorRecipients.map((recipient) => (
                  <span
                    key={recipient._id}
                    className={`max-w-[14rem] truncate rounded-md bg-dashboardColumn px-4 py-2.5 font-josefin text-sm text-secondaryText sm:px-5 ${recipientReceivedRingClass(recipient)}`}
                  >
                    {formatRecipientName(recipient)}
                  </span>
                ))}
              </div>
            )}
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-0 pb-24 pt-1 sm:px-6 lg:px-8 lg:pb-10 lg:pt-2">
            <div className={`letter-editor mx-auto max-w-3xl lg:rounded-xl lg:border lg:border-greyLight/50 lg:bg-white ${isReadOnly ? "letter-editor--readonly" : ""}`}>
              <JoditEditor
                key={selectedLetter}
                value={editorSeed}
                onBlur={blurHandler}
                onChange={changeHandler}
                config={config}
              />
            </div>
          </div>
          <LetterMobileToolbar
            formattingOpen={formatDrawerOpen}
            onOpenFormatting={() => {
              if (formatDrawerOpen) {
                setFormatDrawerOpen(false);
                return;
              }
              saveEditorSelection(joditInstance.current);
              try {
                joditInstance.current?.editor?.blur?.();
              } catch {
                // ignore
              }
              setFormatDrawerOpen(true);
            }}
            onOpenRecipients={() => {
              setFormatDrawerOpen(false);
              setRecipientsSheetOpen(true);
            }}
            readOnly={isReadOnly}
          />
        </div>
      </div>
    </>
  );
}

export default Letter;
